import React, { useState, useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import "../../css/Sidebar.css";
import { auth } from "../../firebase.js";
import Select from "react-select";
import "../../css/WorkspaceSidebar.css"
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import ConvertDocumentModal from "../shared/ConvertDocumentModal";
import NewFromTemplateButton from "../shared/templates/NewFromTemplateButton";
import { useSidebarWidth, SidebarResizeHandle } from "../shared/SidebarResizeHandle";

function WorkspaceSidebar({ canCreate = true, workspaceId, customDomain, isSharedWorkspace = false, workspaceTheme = null, destinationTab = 'documents', onToggleSidebar, hasOnboarding = false, onOpenOnboarding }) {
    const { workspaceCache } = useQuickLinks();
    const { setWorkspaceTheme } = useTheme();
    const [collapsed, setCollapsed] = useState(() => {
        return window.innerWidth > 600;
    });
    const [loading, setLoading] = useState(true);
    const [spaces, setSpaces] = useState([]);
    const [pages, setPages] = useState([]);
    const [walkthroughs, setWalkthroughs] = useState([]);
    const [organizedData, setOrganizedData] = useState([]);
    const [archivedOrganizedData, setArchivedOrganizedData] = useState([]);
    const [workspace, setWorkspace] = useState(null);
    const [hasContent, setHasContent] = useState(true);
    const [uid, setUid] = useState("");
    const [userRole, setUserRole] = useState(null);
    const [workspaceCustomDomain, setWorkspaceCustomDomain] = useState(customDomain);
    const [expandedSpaces, setExpandedSpaces] = useState({});
    const [searchOptions, setSearchOptions] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [ownerIsSubscriber, setOwnerIsSubscriber] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const { width: sidebarWidth, isResizing, setIsResizing, applyWidth, resetWidth } = useSidebarWidth();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(destinationTab === 'walkthroughs' ? 'walkthroughs' : 'documents');
    const { id } = useParams();
    
    // Use workspaceId from props or from URL params
    const currentWorkspaceId = workspaceId || id;

    const handleArchiveWorkspaceItem = async (type, id, isCurrentlyArchived) => {
        const action = isCurrentlyArchived ? 'unarchive' : 'archive';
        const confirmMsg = isCurrentlyArchived
            ? `Unarchive this ${type}?`
            : `Archive this ${type}? All child documents will also be archived.`;
        if (!window.confirm(confirmMsg)) return;
        try {
            const user = auth.currentUser;
            if (!user) { alert("You must be logged in."); return; }
            const token = await user.getIdToken();
            const endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${currentWorkspaceId}/${type === 'space' ? 'spaces' : 'pages'}/${id}/${action}/`;
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${token}`,
                    'Auth-Token': `Bearer ${token}`,
                    'Requesting-User-Uid': user.uid
                }
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || `Failed to ${action} document`);
            }
            window.location.reload();
        } catch (error) {
            console.error(`Error ${action}ing document:`, error);
            alert(`Error: ${error.message}`);
        }
    };

    // Create dynamic context menu options for sidebar items
    const getSidebarItemContextOptions = (itemData) => {
        if (!itemData) return [];

        const { type, id, name, customDomain, isArchived } = itemData;

        // Construct correct URLs based on item type
        let url, editUrl;
        if (type === 'walkthrough') {
            url = `/workspace/${customDomain}/walkthroughs/${id}`;
            editUrl = `/workspace/${customDomain}/edit/walkthrough/${id}`;
        } else {
            url = `/workspace/${customDomain}/${type}/${id}`;
            editUrl = `/workspace/${customDomain}/edit/${type}/${id}/${encodeURIComponent(name)}`;
        }

        const handleDeleteItem = async () => {
            let warningMessage;
            if (type === 'space') {
                warningMessage = "Are you sure you want to delete this space?\n\n-This action cannot be undone.\n-Any pages within this space will be deleted as well.";
            } else if (type === 'walkthrough') {
                warningMessage = "Are you sure you want to delete this walkthrough?\n\n-This action cannot be undone.";
            } else {
                warningMessage = "Are you sure you want to delete this page?\n\n-This action cannot be undone.";
            }
                
            const isConfirmed = window.confirm(warningMessage);
            if (!isConfirmed) {
                return;
            }
            
            try {
                const user = auth.currentUser;
                if (!user) {
                    alert("You must be logged in to delete documents");
                    return;
                }

                const token = await user.getIdToken();
                let endpoint;
                
                if (type === 'space') {
                    endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/`;
                } else if (type === 'walkthrough') {
                    endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/walkthroughs/${id}/`;
                } else {
                    endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/`;
                }
                
                const response = await fetch(endpoint, {
                    method: "DELETE",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `${token}`,
                        'Auth-Token': `Bearer ${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Failed to delete ${type}`);
                }

                alert(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully!`);
                // Refresh the workspace content
                window.location.reload();
            } catch (error) {
                console.error(`Error deleting ${type}:`, error);
                alert(`Error deleting document: ${error.message}`);
            }
        };
        
        const options = [
            {
                label: "Open",
                icon: <i className="fas fa-arrow-right"></i>,
                onClick: () => navigate(url)
            },
            {
                label: "Open in New Tab",
                icon: <i className="fas fa-external-link-alt"></i>,
                url: url,
                onClick: () => window.open(url, "_blank", "noopener,noreferrer")
            },
            {
                label: "Open in New Window",
                icon: <i className="fas fa-window-maximize"></i>,
                onClick: () => window.open(url, "_blank", "width=1200,height=800,noopener,noreferrer")
            },
            {
                label: "Copy Link",
                icon: <i className="fas fa-copy"></i>,
                onClick: () => {
                    const fullUrl = `${window.location.origin}${url}`;
                    navigator.clipboard.writeText(fullUrl);
                    alert("Link copied to clipboard!");
                }
            },
            {
                label: "Convert to Personal…",
                icon: <i className="fas fa-exchange-alt"></i>,
                onClick: () => setShowConvertModal(true)
            }
        ];

        if (type !== 'walkthrough' && (userRole === 'OWNER' || userRole === 'ADMIN')) {
            options.push({
                label: isArchived ? "Unarchive" : "Archive",
                icon: <i className={`fas ${isArchived ? 'fa-box-open' : 'fa-box-archive'}`}></i>,
                onClick: () => handleArchiveWorkspaceItem(type, id, isArchived)
            });
        }

        return options;
    };

    const { showContextMenu, ContextMenuComponent } = useContextMenu(getSidebarItemContextOptions);

    // Wrap showContextMenu to ensure proper event handling
    const handleSidebarItemContextMenu = (e, itemData) => {
        e.preventDefault();
        e.stopPropagation();
        showContextMenu(e.clientX, e.clientY, itemData);
    };

    // Helper function to check if a link is currently active
    const isLinkActive = (linkPath) => {
        return location.pathname === linkPath;
    };

    // Keep the current document visible in the tree when navigating
    useEffect(() => {
        const activeLink = document.querySelector(".sidebar .Sidebar__active-link");
        if (activeLink && typeof activeLink.scrollIntoView === "function") {
            activeLink.scrollIntoView({ block: "nearest" });
        }
    }, [location.pathname, loading]);

    // Auto-expand ancestor nodes when viewing a page or space
    useEffect(() => {
        // Match workspace routes: /workspace/:domain/page/:id or /workspace/:domain/space/:id
        const pageMatch = location.pathname.match(/\/workspace\/[^/]+\/page\/(\d+)/);
        const spaceMatch = location.pathname.match(/\/workspace\/[^/]+\/space\/(\d+)/);
        
        if ((pageMatch || spaceMatch) && organizedData.length > 0) {
            const targetId = parseInt(pageMatch ? pageMatch[1] : spaceMatch[1]);
            
            // Find all ancestor ids for the target page/space
            const findAncestors = (nodes, targetId, path = []) => {
                for (const node of nodes) {
                    const currentPath = [...path, node.id];
                    if (node.id === targetId) {
                        return currentPath;
                    }
                    if (node.children && node.children.length > 0) {
                        const result = findAncestors(node.children, targetId, currentPath);
                        if (result) return result;
                    }
                }
                return null;
            };
            
            const ancestorIds = findAncestors(organizedData, targetId);
            if (ancestorIds) {
                if (spaceMatch) {
                    // If viewing a space, expand the space itself
                    setExpandedSpaces((prev) => ({
                        ...prev,
                        [targetId]: true
                    }));
                } else if (pageMatch && ancestorIds.length > 1) {
                    // If viewing a page, expand all ancestors except the target itself
                    const toExpand = ancestorIds.slice(0, -1);
                    setExpandedSpaces((prev) => {
                        const expanded = { ...prev };
                        toExpand.forEach(id => { expanded[id] = true; });
                        return expanded;
                    });
                }
            }
        }
    }, [location.pathname, organizedData]);

    useEffect(() => {
        // If destinationTab changes (e.g. after navigation), update activeTab accordingly
        if (destinationTab === 'walkthroughs') setActiveTab('walkthroughs');
        else if (destinationTab === 'archived') { setActiveTab('archived'); fetchArchivedContent(); }
        else setActiveTab('documents');
    }, [destinationTab]);

    // Workspace theme override (BBD-81): while inside this workspace its theme
    // wins over the personal theme. Cleared by ThemeRouteSync when leaving
    // workspace routes, so navigating between workspace pages never flashes.
    useEffect(() => {
        if (workspaceTheme !== null && workspaceTheme !== undefined) {
            setWorkspaceTheme(workspaceTheme);
        }
    }, [workspaceTheme, setWorkspaceTheme]);

    const toggleSidebar = () => {
        setCollapsed(prevCollapsed => {
            const newCollapsed = !prevCollapsed;
            if (typeof onToggleSidebar === 'function') {
                onToggleSidebar(newCollapsed);
            }
            return newCollapsed;
        });
    };

    const handleResize = () => {
        setCollapsed(window.innerWidth > 600);
    };

    const toggleSpace = (spaceId) => {
        setExpandedSpaces(prev => ({
            ...prev,
            [spaceId]: !prev[spaceId]
        }));
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                setUid(null);
            }
        });

        return () => { unsubscribe(); };
    }, []);

    useEffect(() => {
        if (!customDomain && workspaceId) {
            const fetchWorkspaceDetails = async () => {
                try {
                    const user = auth.currentUser;
                    if (!user) return;
                    
                    const token = await user.getIdToken();
                    const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/`, {
                        method: "GET",
                        headers: {
                            'Content-Type': 'application/json',
                            'Auth-Token': `Bearer ${token}`,
                            'Authorization': `${token}`,
                            'Requesting-User-Uid': user.uid
                        }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        setWorkspace(data)
                        setWorkspaceCustomDomain(data.custom_domain);
                        setOwnerIsSubscriber(data.owner.is_subscriber)
                    }
                } catch (error) {
                    console.error("Error fetching workspace details:", error);
                }
            };
            
            fetchWorkspaceDetails();
        }
    }, [workspaceId, customDomain]);

    // Fetch workspace spaces and pages when workspace ID is available
    useEffect(() => {
        if (!currentWorkspaceId) return;
        
        const fetchWorkspaceContent = async () => {
            // Check cache first
            if (workspaceCache && workspaceCache.spaces && workspaceCache.pages && workspaceCache.walkthroughs && workspaceCache.timestamp) {
                const cacheAge = Date.now() - workspaceCache.timestamp;
                const cacheMaxAge = 5 * 60 * 1000; // 5 minutes
                
                if (cacheAge < cacheMaxAge && workspaceCache.workspaceDetails && workspaceCache.workspaceDetails.id === currentWorkspaceId) {
                    setSpaces(workspaceCache.spaces);
                    setPages(workspaceCache.pages);
                    setWalkthroughs(workspaceCache.walkthroughs);
                    // Set workspace custom domain from cache if not already set
                    if (!customDomain && workspaceCache.workspaceDetails.custom_domain) {
                        setWorkspaceCustomDomain(workspaceCache.workspaceDetails.custom_domain);
                    }
                    prepareSearchOptions(workspaceCache.spaces, workspaceCache.pages);
                    organizeData(workspaceCache.spaces, workspaceCache.pages);
                    setHasContent(workspaceCache.spaces.length > 0 || workspaceCache.pages.length > 0 || workspaceCache.walkthroughs.length > 0);
                    setLoading(false);
                    return;
                }
            }
            
            setLoading(true);
            try {
                const user = auth.currentUser;
                if (!user) {
                    console.error("User not authenticated");
                    setHasContent(false);
                    setLoading(false);
                    return;
                }
                const token = await user.getIdToken();
                // First fetch workspace details to get user role
                const workspaceResponse = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${currentWorkspaceId}/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Requesting-User-Uid': user.uid,
                        'Authorization': `${token}`,
                    }
                });
                if (!workspaceResponse.ok) {
                    throw new Error(`Failed to fetch workspace: ${workspaceResponse.status}`);
                }
                const workspaceData = await workspaceResponse.json();
                const fetchedUserRole = workspaceData.user_role;
                setUserRole(fetchedUserRole);
                const userRole = fetchedUserRole;
                // Set workspace custom domain if not already set
                if (!customDomain && workspaceData.custom_domain) {
                    setWorkspaceCustomDomain(workspaceData.custom_domain);
                }
                // Fetch spaces
                const spacesResponse = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${currentWorkspaceId}/spaces/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                // Fetch pages
                const pagesResponse = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${currentWorkspaceId}/pages/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                // Fetch walkthroughs
                const walkthroughsResponse = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${currentWorkspaceId}/walkthroughs/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                const spacesData = await spacesResponse.json();
                const pagesData = await pagesResponse.json();
                const walkthroughsData = await walkthroughsResponse.json();
                if (spacesResponse.status === 404 && pagesResponse.status === 404 && walkthroughsResponse.status === 404) {
                    setHasContent(false);
                    setLoading(false);
                    return;
                }
                // Filter out unpublished documents if user is a viewer
                let filteredSpacesData = spacesData;
                let filteredPagesData = pagesData;
                let filteredWalkthroughsData = walkthroughsData;
                if (userRole === 'VIEWER') {
                    // Recursively filter out uncompleted pages from the page tree
                    const filterPagesRecursive = (pagesArr) => {
                        return pagesArr
                            .filter(page => page.completed)
                            .map(page => ({
                                ...page,
                                children: page.children ? filterPagesRecursive(page.children) : null
                            }));
                    };
                    // Filter spaces that have completed = false
                    filteredSpacesData = spacesData.filter(space => space.completed);
                    // Filter pages that have completed = false (top-level)
                    filteredPagesData = pagesData.filter(page => page.completed);
                    // If any pages have children, filter those recursively as well
                    filteredPagesData = filterPagesRecursive(filteredPagesData);
                    // Filter walkthroughs that have completed = false
                    filteredWalkthroughsData = walkthroughsData.filter(walkthrough => walkthrough.completed);
                }
                setSpaces(filteredSpacesData);
                setPages(filteredPagesData);
                setWalkthroughs(filteredWalkthroughsData);
                prepareSearchOptions(filteredSpacesData, filteredPagesData);
                // Organize the data with filtered spaces and pages
                organizeData(filteredSpacesData, filteredPagesData);
                setHasContent(filteredSpacesData.length > 0 || filteredPagesData.length > 0 || filteredWalkthroughsData.length > 0);
            } catch (error) {
                console.error("Error fetching workspace content:", error);
                setHasContent(false);
            } finally {
                setLoading(false);
            }
        };
        fetchWorkspaceContent();
    }, [currentWorkspaceId, workspaceCache]);
    
    const prepareSearchOptions = (spacesData, pagesData) => {
        const spaceOptions = spacesData.map(space => ({
            value: {
                id: space.id,
                type: 'space',
                name: space.name,
                workspaceId: currentWorkspaceId
            },
            label: `Space: ${space.name}`
        }));
        
        const pageOptions = pagesData.map(page => ({
            value: {
                id: page.id,
                type: 'page',
                name: page.page_name,
                workspaceId: currentWorkspaceId
            },
            label: `Page: ${page.page_name}`
        }));
        
        setSearchOptions([...spaceOptions, ...pageOptions]);
    };

    const handleSearchClick = () => { 
        setShowSearch(!showSearch);
    }
    
    const handleSearchSelect = (selected) => {
        if (selected) {
            const { type, id } = selected.value;
            navigate(`/workspace/${workspaceCustomDomain}/${type}/${id}`);
            setShowSearch(false);
        }
    }

    const organizeData = (spacesData, pagesData) => {
        // Create a map of all spaces for quick access
        const spaceMap = new Map();
        const pageMap = new Map();
        const organized = [];
        
        // Sort spaces by name for consistent ordering
        const sortedSpaces = [...spacesData].sort((a, b) => a.name.localeCompare(b.name));
        
        // First, create entries for all spaces and add them to the organized array
        sortedSpaces.forEach(space => {
            const spaceEntry = {
                id: space.id,
                name: space.name,
                type: 'space',
                children: []
            };
            spaceMap.set(space.id, spaceEntry);
            organized.push(spaceEntry);
        });
        
        // Sort pages by page_name for consistent ordering
        const sortedPages = [...pagesData].sort((a, b) => a.page_name.localeCompare(b.page_name));
        
        // Second, create entries for all pages but don't organize them yet
        sortedPages.forEach(page => {
            const pageEntry = {
                id: page.id,
                name: page.page_name,
                type: 'page',
                parentId: page.parent_page ? page.parent_page.id : null,
                spaceId: page.space ? page.space.id : null,
                children: []
            };
            pageMap.set(page.id, pageEntry);
        });
        
        // Third, organize pages based on their parent relationships
        sortedPages.forEach(page => {
            const pageEntry = pageMap.get(page.id);
            
            if (page.space && spaceMap.has(page.space.id)) {
                // This page belongs directly under a space
                spaceMap.get(page.space.id).children.push(pageEntry);
            } else if (page.parent_page && pageMap.has(page.parent_page.id)) {
                // This page belongs under another page
                pageMap.get(page.parent_page.id).children.push(pageEntry);
            } else {
                // This is a top-level page or has a missing parent
                organized.push(pageEntry);
            }
        });
        
        // Sort children arrays for consistent ordering
        const sortChildren = (item) => {
            if (item.children && item.children.length > 0) {
                item.children.sort((a, b) => a.name.localeCompare(b.name));
                item.children.forEach(sortChildren);
            }
        };
        
        organized.forEach(sortChildren);
        
        setOrganizedData(organized);
    };    
    
    const organizeArchivedData = (spacesData, pagesData) => {
        const spaceMap = new Map();
        const pageMap = new Map();
        const organized = [];

        const sortedSpaces = [...spacesData].sort((a, b) => a.name.localeCompare(b.name));
        sortedSpaces.forEach(space => {
            const spaceEntry = { id: space.id, name: space.name, type: 'space', isArchived: true, children: [] };
            spaceMap.set(space.id, spaceEntry);
            organized.push(spaceEntry);
        });

        const sortedPages = [...pagesData].sort((a, b) => a.page_name.localeCompare(b.page_name));
        sortedPages.forEach(page => {
            const pageEntry = {
                id: page.id, name: page.page_name, type: 'page', isArchived: true,
                parentId: page.parent_page ? page.parent_page.id : null,
                spaceId: page.space ? page.space.id : null,
                children: []
            };
            pageMap.set(page.id, pageEntry);
        });

        sortedPages.forEach(page => {
            const pageEntry = pageMap.get(page.id);
            // Only nest under a space if that space is also archived (present in spaceMap)
            if (page.space && spaceMap.has(page.space.id)) {
                spaceMap.get(page.space.id).children.push(pageEntry);
            } else if (page.parent_page && pageMap.has(page.parent_page.id)) {
                pageMap.get(page.parent_page.id).children.push(pageEntry);
            } else {
                organized.push(pageEntry);
            }
        });

        const sortChildren = (item) => {
            if (item.children && item.children.length > 0) {
                item.children.sort((a, b) => a.name.localeCompare(b.name));
                item.children.forEach(sortChildren);
            }
        };
        organized.forEach(sortChildren);
        setArchivedOrganizedData(organized);
    };

    const fetchArchivedContent = async () => {
        if (!currentWorkspaceId) return;
        try {
            const user = auth.currentUser;
            if (!user) return;
            const token = await user.getIdToken();
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${currentWorkspaceId}/archived/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': user.uid
                }
            });
            if (!response.ok) return;
            const data = await response.json();
            organizeArchivedData(data.spaces || [], data.pages || []);
        } catch (error) {
            console.error("Error fetching archived content:", error);
        }
    };

    const findAndAttachToParent = (items, orphan) => {
        for (const item of items) {
            if (item.id === orphan.parentId && item.type === 'page') {
                item.children.push(orphan);
                return true;
            }
            
            if (item.children.length > 0) {
                if (findAndAttachToParent(item.children, orphan)) {
                    return true;
                }
            }
        }
        return false;
    };  

    const handleNavigateClick = () => { 
        navigate(`/workspace/create/document`);
    }

    const handleSharedNavigateClick = () => {
        navigate(`/workspace/${customDomain}/create/document`)
    }
    
    const handleHelpClick = () => { 
        navigate(`/workspace/${workspaceCustomDomain}/settings`);
    }

    return (
        <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${isResizing ? 'resizing' : ''}`} style={{ "--sidebar-width": `${sidebarWidth}px` }}>
            <button onClick={toggleSidebar} className="toggle-button">
                <i className={`fas ${collapsed ? 'fa-arrow-left' : 'fa-arrow-right'}`}></i>
            </button>

            <div className={`WorkspaceSidebar__icon-container Sidebar__icon-container ${collapsed ? 'Sidebar__icon-container': 'Sidebar__hide-content'}`}>
                <Link to="/">
                    <i className="fas fa-home Sidebar__icons" title="Home"></i>
                </Link>
                <Link to={`/workspace/${workspaceCustomDomain}`}>
                    <i className="fa-solid fa-table-columns Sidebar__icons" title="Dashboard"></i>
                </Link>
                <i
                    className={`fa-solid fa-file Sidebar__icons${activeTab === 'documents' ? ' Sidebar__icon-active' : ''}`}
                    title="Documents"
                    onClick={() => setActiveTab('documents')}
                    style={{ cursor: 'pointer' }}
                ></i>
                <i
                    className={`fa-solid fa-book-open Sidebar__icons${activeTab === 'walkthroughs' ? ' Sidebar__icon-active' : ''}`}
                    title="Walkthroughs"
                    onClick={() => setActiveTab('walkthroughs')}
                    style={{ cursor: 'pointer' }}
                ></i>
                <i
                    className={`fa-solid fa-box-archive Sidebar__icons${activeTab === 'archived' ? ' Sidebar__icon-active' : ''}`}
                    title="Archived"
                    onClick={() => { setActiveTab('archived'); fetchArchivedContent(); }}
                    style={{ cursor: 'pointer' }}
                ></i>
            </div>

            {/* Only show search for documents tab */}
            {activeTab === 'documents' && (
                <div className={`Sidebar__search-dropdown ${!collapsed ? 'Sidebar__search-dropdown-collapsed' : ''}`}> 
                    <Select
                        options={searchOptions}
                        onChange={handleSearchSelect}
                        placeholder="Workspace Documents"
                        isClearable
                        className="Sidebar__search-select"
                        menuPortalTarget={document.body}
                        styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({
                                ...base,
                                backgroundColor: 'var(--bb-bg)',
                                borderColor: 'var(--bb-text)',
                            }),
                            menu: (base) => ({
                                ...base,
                                backgroundColor: 'var(--bb-bg)',
                            }),
                            option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused 
                                    ? 'var(--bb-border)' 
                                    : 'var(--bb-bg)',
                                color: 'var(--bb-accent)',
                            }),
                        }}
                    />
                </div>
            )}

            {loading ? (
                <div className={`WorkspaceSidebar__loading ${collapsed ? '': 'Sidebar__hide-content'}`}>Loading...</div>
            ) : (
                <nav className={`${collapsed ? '': 'Sidebar__hide-content'}`}>
                    {activeTab === 'documents' ? (
                        hasContent ? (
                            <ul className="Sidebar__ul">
                                {/* Render spaces directly in the main ul */}
                                {organizedData.map((item) => (
                                    <React.Fragment key={`${item.type}-${item.id}`}>
                                        {item.type === 'space' ? (
                                            <li>
                                                <div className="Sidebar__item-container">
                                                    {item.children && item.children.length > 0 && (
                                                        <i
                                                            className={`fas ${expandedSpaces[item.id] ? 'fa-folder-open' : 'fa-folder'} Sidebar__space-icon Sidebar__space-icon-clickable`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                toggleSpace(item.id);
                                                            }}
                                                            title="Click to expand/collapse"
                                                        ></i>
                                                    )}
                                                    {(!item.children || item.children.length === 0) && (
                                                        <i className="fas fa-folder Sidebar__space-icon"></i>
                                                    )}
                                                    <Link
                                                        to={`/workspace/${workspaceCustomDomain}/${item.type}/${item.id}`}
                                                        className={`Sidebar__link-wrapper ${isLinkActive(`/workspace/${workspaceCustomDomain}/${item.type}/${item.id}`) ? 'Sidebar__active-link' : ''}`}
                                                        onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                            type: item.type,
                                                            id: item.id,
                                                            name: item.name,
                                                            customDomain: workspaceCustomDomain,
                                                            isArchived: false
                                                        })}
                                                    >
                                                        <p className="Sidebar__link" title={item.name}>{item.name}</p>
                                                    </Link>
                                                    <button
                                                        className="Sidebar__edit-button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            navigate(`/workspace/${workspaceCustomDomain}/edit/${item.type}/${item.id}/${encodeURIComponent(item.name)}`);
                                                        }}
                                                        title="Edit document"
                                                    >
                                                        <i className="fas fa-pen"></i>
                                                    </button>
                                                </div>
                                                {/* Render children of spaces only if expanded */}
                                                {item.children && item.children.length > 0 && expandedSpaces[item.id] && (
                                                    <ul>
                                                        {item.children.map((child) => (
                                                            <PageItem
                                                                key={`${child.type}-${child.id}`}
                                                                item={child}
                                                                workspaceId={workspaceId}
                                                                customDomain={workspaceCustomDomain}
                                                                expandedSpaces={expandedSpaces}
                                                                toggleSpace={toggleSpace}
                                                                isLinkActive={isLinkActive}
                                                                onItemRightClick={handleSidebarItemContextMenu}
                                                            />
                                                        ))}
                                                    </ul>
                                                )}
                                            </li>
                                        ) : (
                                            /* Render top-level pages */
                                            <PageItem
                                                item={item}
                                                workspaceId={workspaceId}
                                                customDomain={workspaceCustomDomain}
                                                expandedSpaces={expandedSpaces}
                                                toggleSpace={toggleSpace}
                                                isLinkActive={isLinkActive}
                                                onItemRightClick={handleSidebarItemContextMenu}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </ul>
                        ) : (
                            <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>
                                No content in this workspace.
                            </p>
                        )
                    ) : activeTab === 'walkthroughs' ? (
                        // Walkthroughs tab
                        walkthroughs && walkthroughs.length > 0 ? (
                            <ul className="Sidebar__ul">
                                {walkthroughs.map((wt) => (
                                    <li key={wt.id}>
                                        <div className="Sidebar__item-container">
                                            <Link
                                                to={`/workspace/${workspaceCustomDomain}/walkthroughs/${wt.id}`}
                                                className={`Sidebar__link-wrapper ${isLinkActive(`/workspace/${workspaceCustomDomain}/walkthroughs/${wt.id}`) ? 'Sidebar__active-link' : ''}`}
                                                onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                    type: 'walkthrough',
                                                    id: wt.id,
                                                    name: wt.walkthrough_name,
                                                    customDomain: workspaceCustomDomain
                                                })}
                                            >
                                                <p className="Sidebar__link" title={wt.walkthrough_name}>{wt.walkthrough_name}</p>
                                            </Link>
                                            <button
                                                className="Sidebar__edit-button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    navigate(`/workspace/${workspaceCustomDomain}/edit/walkthrough/${wt.id}`);
                                                }}
                                                title="Edit walkthrough"
                                            >
                                                <i className="fas fa-pen"></i>
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>
                                No walkthroughs in this workspace.
                            </p>
                        )
                    ) : (
                        // Archived tab
                        archivedOrganizedData && archivedOrganizedData.length > 0 ? (
                            <ul className="Sidebar__ul">
                                {archivedOrganizedData.map((item) => (
                                    <React.Fragment key={`arch-${item.type}-${item.id}`}>
                                        {item.type === 'space' ? (
                                            <li>
                                                <div className="Sidebar__item-container">
                                                    {item.children && item.children.length > 0 && (
                                                        <i
                                                            className={`fas ${expandedSpaces[`arch_${item.id}`] ? 'fa-folder-open' : 'fa-folder'} Sidebar__space-icon Sidebar__space-icon-clickable`}
                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSpace(`arch_${item.id}`); }}
                                                            title="Click to expand/collapse"
                                                        ></i>
                                                    )}
                                                    {(!item.children || item.children.length === 0) && (
                                                        <i className="fas fa-folder Sidebar__space-icon"></i>
                                                    )}
                                                    <Link
                                                        to={`/workspace/${workspaceCustomDomain}/${item.type}/${item.id}`}
                                                        className={`Sidebar__link-wrapper ${isLinkActive(`/workspace/${workspaceCustomDomain}/${item.type}/${item.id}`) ? 'Sidebar__active-link' : ''}`}
                                                        onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                            type: item.type,
                                                            id: item.id,
                                                            name: item.name,
                                                            customDomain: workspaceCustomDomain,
                                                            isArchived: true
                                                        })}
                                                    >
                                                        <p className="Sidebar__link" title={item.name}>{item.name}</p>
                                                    </Link>
                                                </div>
                                                {item.children && item.children.length > 0 && expandedSpaces[`arch_${item.id}`] && (
                                                    <ul>
                                                        {item.children.map((child) => (
                                                            <PageItem
                                                                key={`arch-${child.type}-${child.id}`}
                                                                item={child}
                                                                workspaceId={workspaceId}
                                                                customDomain={workspaceCustomDomain}
                                                                expandedSpaces={expandedSpaces}
                                                                toggleSpace={toggleSpace}
                                                                isLinkActive={isLinkActive}
                                                                onItemRightClick={handleSidebarItemContextMenu}
                                                                isArchived={true}
                                                            />
                                                        ))}
                                                    </ul>
                                                )}
                                            </li>
                                        ) : (
                                            <PageItem
                                                item={item}
                                                workspaceId={workspaceId}
                                                customDomain={workspaceCustomDomain}
                                                expandedSpaces={expandedSpaces}
                                                toggleSpace={toggleSpace}
                                                isLinkActive={isLinkActive}
                                                onItemRightClick={handleSidebarItemContextMenu}
                                                isArchived={true}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </ul>
                        ) : (
                            <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>
                                No archived documents.
                            </p>
                        )
                    )}
                </nav>
            )}

            {collapsed && (
                <>
                    {canCreate && currentWorkspaceId && activeTab === 'documents' && (
                        isSharedWorkspace ? (
                            <div className="Sidebar__create-actions">
                                <button className="Sidebar__create-button" onClick={handleSharedNavigateClick}>
                                    <i className="fas fa-plus Sidebar__plus-sign"></i>  Create
                                </button>
                                <NewFromTemplateButton
                                    createPath={`/workspace/${customDomain}/create/document`}
                                    workspaceId={currentWorkspaceId}
                                    className="Sidebar__create-button Sidebar__create-button--template"
                                >
                                    <i className="fas fa-puzzle-piece Sidebar__plus-sign"></i>  Templates
                                </NewFromTemplateButton>
                            </div>
                        ) : (
                            <div className="Sidebar__create-actions">
                                <button className="Sidebar__create-button" onClick={handleNavigateClick}>
                                    <i className="fas fa-plus Sidebar__plus-sign"></i>  Create
                                </button>
                                <NewFromTemplateButton
                                    createPath={(customDomain || workspaceCustomDomain) ? `/workspace/${customDomain || workspaceCustomDomain}/create/document` : '/workspace/create/document'}
                                    workspaceId={currentWorkspaceId}
                                    className="Sidebar__create-button Sidebar__create-button--template"
                                >
                                    <i className="fas fa-puzzle-piece Sidebar__plus-sign"></i>  Templates
                                </NewFromTemplateButton>
                            </div>
                        )
                    )}
                    {canCreate && currentWorkspaceId && activeTab === 'walkthroughs' && (
                        <button
                            className="Sidebar__create-button"
                            onClick={() => navigate(`/workspace/${workspaceCustomDomain}/create/walkthrough`)}
                        >
                            <i className="fas fa-plus Sidebar__plus-sign"></i>  Create
                        </button>
                    )}
                    <div className="Sidebar__bottom-icons">
                        {hasOnboarding && onOpenOnboarding && (
                            <i className="fas fa-rocket Sidebar__help" onClick={onOpenOnboarding} title="Getting Started"></i>
                        )}
                        <i className="fa-solid fa-gear Sidebar__help" onClick={handleHelpClick} title="Workspace Settings"></i>
                    </div>
                </>
            )}
            {ContextMenuComponent}
            {showConvertModal && (
                <ConvertDocumentModal
                    isOpen={showConvertModal}
                    onClose={() => setShowConvertModal(false)}
                    preSelectedDirection="workspace_to_personal"
                    preSelectedWorkspaceId={currentWorkspaceId ? Number(currentWorkspaceId) : null}
                />
            )}
            {collapsed && (
                <SidebarResizeHandle
                    width={sidebarWidth}
                    applyWidth={applyWidth}
                    resetWidth={resetWidth}
                    setIsResizing={setIsResizing}
                />
            )}
        </div>
    );
}

function PageItem({ item, workspaceId, customDomain, expandedSpaces, toggleSpace, isLinkActive, onItemRightClick, isArchived = false }) {
    const navigate = useNavigate();
    const expandKey = isArchived ? `arch_${item.id}` : item.id;
    const isExpanded = expandedSpaces ? expandedSpaces[expandKey] || false : false;
    const linkPath = `/workspace/${customDomain}/${item.type}/${item.id}`;
    const editPath = `/workspace/${customDomain}/edit/${item.type}/${item.id}/${encodeURIComponent(item.name)}`;

    const handleEditClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(editPath);
    };

    const handleContextMenu = (e) => {
        if (onItemRightClick) {
            e.preventDefault();
            e.stopPropagation();
            onItemRightClick(e, {
                type: item.type,
                id: item.id,
                name: item.name,
                customDomain: customDomain,
                isArchived: isArchived
            });
        }
    };
    
    return (
        <li>
            <div className="Sidebar__item-container">
                {/* Show the caret for pages with children */}
                {item.children && item.children.length > 0 && toggleSpace ? (
                    <button
                        className="Sidebar__chevron-button"
                        onClick={() => toggleSpace(expandKey)}
                        title={isExpanded ? "Collapse" : "Expand"}
                    >
                        <i className={`Sidebar__icon-size fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                    </button>
                ) : (
                    <i className="fas fa-file Sidebar__page-icon"></i>
                )}
                <Link
                    to={linkPath}
                    className={`Sidebar__link-wrapper ${isLinkActive && isLinkActive(linkPath) ? 'Sidebar__active-link' : ''}`}
                    onContextMenu={handleContextMenu}
                >
                    <p className="Sidebar__link" title={item.name}>{item.name}</p>
                </Link>
                {!isArchived && (
                    <button
                        className="Sidebar__edit-button"
                        onClick={handleEditClick}
                        title="Edit document"
                    >
                        <i className="fas fa-pen"></i>
                    </button>
                )}
            </div>
            {/* Only show children if expanded */}
            {isExpanded && item.children && item.children.length > 0 && (
                <ul>
                    {item.children.map((child) => (
                        <PageItem
                            key={`${isArchived ? 'arch-' : ''}${child.type}-${child.id}`}
                            item={child}
                            workspaceId={workspaceId}
                            customDomain={customDomain}
                            expandedSpaces={expandedSpaces}
                            toggleSpace={toggleSpace}
                            isLinkActive={isLinkActive}
                            onItemRightClick={onItemRightClick}
                            isArchived={isArchived}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

export default WorkspaceSidebar;